import React, { useEffect, useState } from 'react';
import { adminApi } from '../../services/adminApi';
import { PlatformAdminListItem } from '../../types';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Plus, ShieldCheck, ShieldOff, CheckCircle2, XCircle } from 'lucide-react';

export function AdminPortalAdmins() {
  const { admin: me } = useAdminAuth();
  const [admins, setAdmins] = useState<PlatformAdminListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ email: '', fullName: '', password: '' });
  const [creating, setCreating] = useState(false);
  const [toggling, setToggling] = useState<string | null>(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      const { admins } = await adminApi.listAdmins();
      setAdmins(admins);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setCreating(true);
    try {
      await adminApi.createAdmin({
        email: form.email.trim(),
        fullName: form.fullName.trim(),
        password: form.password,
      });
      setShowForm(false);
      setForm({ email: '', fullName: '', password: '' });
      await load();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleToggle = async (a: PlatformAdminListItem) => {
    const isActive = !!a.is_active;
    if (isActive && !confirm(`Deactivate ${a.email}? They will no longer be able to log in.`)) return;
    setError('');
    setToggling(a.id);
    try {
      if (isActive) {
        await adminApi.deactivateAdmin(a.id);
      } else {
        await adminApi.reactivateAdmin(a.id);
      }
      await load();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setToggling(null);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-96 text-slate-400 text-lg">Loading…</div>;

  return (
    <div className="max-w-4xl mx-auto pb-24 p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Platform Admins</h1>
          <p className="text-slate-500 font-medium mt-1">Manage who has admin portal access.</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-5 py-3.5 bg-slate-900 text-white rounded-2xl font-bold hover:bg-black transition-all active:scale-95 shadow-md"
        >
          <Plus className="w-5 h-5" />
          Add Admin
        </button>
      </div>

      {error && (
        <div className="px-5 py-4 bg-red-50 text-red-600 border border-red-100 rounded-2xl mb-6 font-medium">
          {error}
        </div>
      )}

      {showForm && (
        <div className="bg-white p-8 rounded-[2rem] mb-8 shadow-xl shadow-slate-200/40 border border-slate-200/60">
          <h2 className="text-xl font-bold text-slate-800 mb-6">Add Platform Admin</h2>
          <form onSubmit={handleCreate} className="flex flex-col gap-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Full Name *">
                <input className={inputCls} type="text" placeholder="Jane Doe" value={form.fullName} onChange={e => setForm({ ...form, fullName: e.target.value })} required />
              </Field>
              <Field label="Email *">
                <input className={inputCls} type="email" placeholder="jane@company.com" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required />
              </Field>
              <Field label="Password *">
                <input className={inputCls} type="password" placeholder="Temporary password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required minLength={8} />
              </Field>
            </div>
            <div className="flex gap-3 mt-2">
              <button type="submit" disabled={creating} className="px-6 py-3.5 bg-slate-900 text-white rounded-2xl font-bold hover:bg-black transition-all active:scale-95 disabled:opacity-50">
                {creating ? 'Adding…' : 'Add Admin'}
              </button>
              <button type="button" onClick={() => { setShowForm(false); setError(''); }} className="px-6 py-3.5 bg-white text-slate-600 border border-slate-200 rounded-2xl font-bold hover:bg-slate-50 transition-all active:scale-95">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-[2rem] shadow-sm border border-slate-200/60 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50/50">
                <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-widest">Admin</th>
                <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-widest">Status</th>
                <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-widest">Added</th>
                <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-widest text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {admins.map((a) => {
                const isActive = !!a.is_active;
                const isMe = a.email === me?.email;
                return (
                  <tr key={a.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/30">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <ShieldCheck className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <div>
                          <div className="font-bold text-slate-800 text-[14px]">{a.full_name}</div>
                          <div className="text-[13px] text-slate-500">{a.email}</div>
                        </div>
                        {isMe && (
                          <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-500 uppercase tracking-wide border border-slate-200">
                            You
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {isActive ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 uppercase tracking-wide">
                          <CheckCircle2 className="w-3 h-3" /> Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-slate-100 text-slate-500 border border-slate-200 uppercase tracking-wide">
                          <XCircle className="w-3 h-3" /> Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-[13px] text-slate-500">
                      {new Date(a.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-right">
                      {!isMe && (
                        <button
                          onClick={() => handleToggle(a)}
                          disabled={toggling === a.id}
                          className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[13px] font-bold transition-all active:scale-95 disabled:opacity-50 ${
                            isActive
                              ? 'bg-white text-slate-500 border border-slate-200 hover:bg-red-50 hover:text-red-600 hover:border-red-200'
                              : 'bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100'
                          }`}
                        >
                          {isActive ? <ShieldOff className="w-4 h-4" /> : <ShieldCheck className="w-4 h-4" />}
                          {toggling === a.id ? '…' : isActive ? 'Deactivate' : 'Reactivate'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const inputCls = 'w-full h-14 px-5 bg-white border border-slate-200 rounded-2xl text-[16px] text-slate-800 font-medium focus:outline-none focus:border-slate-900 focus:ring-1 focus:ring-slate-900 transition-all placeholder-slate-300';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-[13px] font-bold text-slate-500 uppercase tracking-wide mb-2 ml-1">
        {label}
      </label>
      {children}
    </div>
  );
}
